import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/client.js";
import { useI18n } from "../i18n/I18nContext.jsx";
import ZoneForm from "../components/ZoneForm.jsx";
import Pagination from "../components/Pagination.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { useSetPageTitle } from "../context/PageTitleContext.jsx";
import "../styles/ZoneProfile.css";

function formatINR(n) {
  if (n === null || n === undefined || n === "") return "—";
  return "₹" + Number(n).toLocaleString("en-IN");
}

export default function ZoneProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user } = useAuth();
  
  const [zone, setZone] = useState(null);
  const [stats, setStats] = useState(null);
  const [paras, setParas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [parasLoading, setParasLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  // Advanced Filters State
  const [statusFilter, setStatusFilter] = useState("PENDING,IN_PROGRESS,REOPENED");
  const [assignFilter, setAssignFilter] = useState("");
  const [amountFilter, setAmountFilter] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const [activeTab, setActiveTab] = useState("pending");

  // Fetch Zone Details & Statistics
  const fetchZoneAndStats = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/zones/${id}/`),
      api.get(`/zones/${id}/stats/`)
    ])
      .then(([zoneRes, statsRes]) => {
        setZone(zoneRes.data);
        setStats(statsRes.data);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to fetch zone details.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Fetch paginated, filtered paras list
  const fetchFilteredParas = useCallback(() => {
    setParasLoading(true);
    const params = {
      zone: id,
      page: page,
      page_size: 8,
    };
    if (statusFilter) params.status = statusFilter;
    if (assignFilter) params.assign_type = assignFilter;
    if (amountFilter) params.amount_filter = amountFilter;

    api.get("/paras/", { params })
      .then((res) => {
        setParas(res.data.results || res.data);
        setCount(res.data.count ?? (res.data.results || res.data).length);
      })
      .catch((err) => {
        console.error("Failed to fetch filtered paras:", err);
      })
      .finally(() => setParasLoading(false));
  }, [id, page, statusFilter, assignFilter, amountFilter]);

  useEffect(() => {
    fetchZoneAndStats();
  }, [fetchZoneAndStats]);

  useEffect(() => {
    fetchFilteredParas();
  }, [fetchFilteredParas]);

  useSetPageTitle(zone ? `${t("zoneProfile")} - ${zone.name}` : null);

  const handleDelete = async () => {
    if (!window.confirm(t("deleteConfirm"))) return;
    try {
      await api.delete(`/zones/${id}/`);
      navigate("/zones");
    } catch (err) {
      console.error(err);
      alert("Failed to delete zone.");
    }
  };

  if (loading) return <div className="center-screen">{t("loading")}</div>;
  if (error) return <div className="center-screen">{error}</div>;
  if (!zone || !stats) return <div className="center-screen">Zone not found.</div>;

  const isAdmin = user?.role === "SUPER_ADMIN" || user?.role === "CORP_ADMIN";

  // Counts from dynamic backend stats
  const totalParas = stats.total_paras;
  const pendingParas = stats.pending_paras;
  const resolvedParas = stats.resolved_paras;
  const amountPending = stats.financials.amount_pending;
  const totalAmount = stats.financials.total_amount;
  const amountRecovered = stats.financials.amount_recovered;

  const pendingCount = stats.status_counts.PENDING;
  const inProgressCount = stats.status_counts.IN_PROGRESS;
  const replyCount = stats.status_counts.REPLY_SUBMITTED;
  const resolvedCount = stats.status_counts.RESOLVED;
  const closedCount = stats.status_counts.CLOSED;
  const reopenedCount = stats.status_counts.REOPENED;

  const unassignedCount = stats.assign_counts.UNASSIGNED;
  const individualCount = stats.assign_counts.INDIVIDUAL;
  const combinedCount = stats.assign_counts.COMBINED;
  const multipleCount = stats.assign_counts.MULTIPLE;

  const statusBreakdown = [
    { label: "Pending",         count: pendingCount,    color: "#f59e0b", bg: "#fffbeb", icon: "⏳", status: "PENDING" },
    { label: "In Progress",     count: inProgressCount, color: "#3b82f6", bg: "#eff6ff", icon: "🔄", status: "IN_PROGRESS" },
    { label: "Reply Submitted", count: replyCount,      color: "#8b5cf6", bg: "#f5f3ff", icon: "📝", status: "REPLY_SUBMITTED" },
    { label: "Resolved",        count: resolvedCount,   color: "#10b981", bg: "#f0fdf4", icon: "✅", status: "RESOLVED" },
    { label: "Closed",          count: closedCount,     color: "#059669", bg: "#ecfdf5", icon: "🔒", status: "CLOSED" },
    { label: "Reopened",        count: reopenedCount,   color: "#ef4444", bg: "#fef2f2", icon: "🔁", status: "REOPENED" },
  ];

  const assignBreakdown = [
    { label: "Unassigned",     count: unassignedCount, color: "#94a3b8", icon: "⬜", type: "UNASSIGNED" },
    { label: "Individual",     count: individualCount, color: "#3b82f6", icon: "👤", type: "INDIVIDUAL" },
    { label: "Zone (Combined)",count: combinedCount,   color: "#8b5cf6", icon: "🗺️", type: "COMBINED" },
    { label: "Multi-Officer",  count: multipleCount,   color: "#f59e0b", icon: "👥", type: "MULTIPLE" },
  ];

  const totalPages = Math.ceil(count / 8);

  // Navigation handlers for filter cards
  const selectTotalParas = () => {
    setStatusFilter("");
    setAmountFilter("");
    setAssignFilter("");
    setActiveTab("all");
    setPage(1);
  };

  const selectPendingParas = () => {
    setStatusFilter("PENDING,IN_PROGRESS,REOPENED");
    setAmountFilter("");
    setAssignFilter("");
    setActiveTab("pending");
    setPage(1);
  };

  const selectResolvedParas = () => {
    setStatusFilter("RESOLVED,CLOSED");
    setAmountFilter("");
    setAssignFilter("");
    setActiveTab("closed");
    setPage(1);
  };

  const selectAmountPending = () => {
    setStatusFilter("PENDING,IN_PROGRESS,REOPENED");
    setAmountFilter("with_amount");
    setAssignFilter("");
    setActiveTab("pending");
    setPage(1);
  };

  const handleAssignClick = (type) => {
    setAssignFilter(prev => prev === type ? "" : type);
    setPage(1);
  };

  const handleAmountClick = (type) => {
    setAmountFilter(prev => prev === type ? "" : type);
    setPage(1);
  };

  // Helper check for active cards/rows
  const isTotalActive = statusFilter === "" && amountFilter === "" && assignFilter === "";
  const isPendingActive = statusFilter === "PENDING,IN_PROGRESS,REOPENED" && amountFilter === "" && assignFilter === "";
  const isResolvedActive = statusFilter === "RESOLVED,CLOSED" && amountFilter === "" && assignFilter === "";
  const isAmountPendingActive = statusFilter === "PENDING,IN_PROGRESS,REOPENED" && amountFilter === "with_amount" && assignFilter === "";

  return (
    <div className="page-zone-profile">
      {/* ── Header ── */}
      <div className="page-header">
        <button className="btn btn-outline" onClick={() => navigate(-1)}>
          &larr; Back
        </button>
        {isAdmin && (
          <div style={{ display: "flex", gap: "0.5rem", marginLeft: "auto" }}>
            <button className="btn btn-primary" onClick={() => setShowEdit(true)}>
              {t("editZone")}
            </button>
            <button className="btn btn-outline" style={{ borderColor: "red", color: "red" }} onClick={handleDelete}>
              {t("deleteZone")}
            </button>
          </div>
        )}
      </div>

      {/* ── Top Summary Cards ── */}
      <div className="stat-grid" style={{ marginBottom: "2rem" }}>
        <div 
          className={`stat-card clickable ${isTotalActive ? "active" : ""}`} 
          onClick={selectTotalParas}
        >
          <div className="stat-value">{totalParas}</div>
          <div className="stat-label">{t("totalParas")}</div>
        </div>
        <div 
          className={`stat-card red clickable ${isPendingActive ? "active" : ""}`}
          onClick={selectPendingParas}
        >
          <div className="stat-value">{pendingParas}</div>
          <div className="stat-label">{t("pendingParas")}</div>
        </div>
        <div 
          className={`stat-card green clickable ${isResolvedActive ? "active" : ""}`}
          onClick={selectResolvedParas}
        >
          <div className="stat-value">{resolvedParas}</div>
          <div className="stat-label">{t("resolvedParas")}</div>
        </div>
        <div 
          className={`stat-card red clickable ${isAmountPendingActive ? "active" : ""}`}
          onClick={selectAmountPending}
        >
          <div className="stat-value" style={{ fontSize: 20 }}>
            {formatINR(amountPending)}
          </div>
          <div className="stat-label">{t("amountPending")}</div>
        </div>
      </div>

      {/* ── Assignment Breakdown + Financial Summary ── */}
      <div className="zp-two-col" style={{ marginBottom: "1.5rem" }}>

        <div className="card">
          <h3 style={{ margin: "0 0 1rem" }}>🗂️ Assignment Breakdown</h3>
          <div className="zp-assign-list">
            {assignBreakdown.map(item => {
              const isActive = assignFilter === item.type;
              return (
                <div 
                  key={item.label} 
                  className={`zp-assign-row clickable ${isActive ? "active" : ""}`}
                  onClick={() => handleAssignClick(item.type)}
                >
                  <div className="zp-assign-left">
                    <span className="zp-assign-icon">{item.icon}</span>
                    <span className="zp-assign-label">{item.label}</span>
                  </div>
                  <div className="zp-assign-bar-wrap">
                    <div
                      className="zp-assign-bar-fill"
                      style={{
                        width: totalParas > 0 ? `${Math.round((item.count / totalParas) * 100)}%` : "0%",
                        background: item.color,
                      }}
                    />
                  </div>
                  <span className="zp-assign-count" style={{ color: item.color }}>{item.count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <h3 style={{ margin: "0 0 1rem" }}>💰 Financial Summary</h3>
          <div className="zp-finance-list">
            <div 
              className={`zp-finance-row clickable ${isTotalActive ? "active" : ""}`}
              onClick={selectTotalParas}
            >
              <span className="zp-finance-label">Total Amount</span>
              <span className="zp-finance-value" style={{ color: "#0f172a" }}>{formatINR(totalAmount)}</span>
            </div>
            <div 
              className={`zp-finance-row clickable ${isAmountPendingActive ? "active" : ""}`}
              onClick={selectAmountPending}
            >
              <span className="zp-finance-label">Amount Pending</span>
              <span className="zp-finance-value" style={{ color: "#ef4444" }}>{formatINR(amountPending)}</span>
            </div>
            <div 
              className={`zp-finance-row clickable ${statusFilter === "RESOLVED,CLOSED" && amountFilter === "with_amount" ? "active" : ""}`}
              onClick={() => {
                setStatusFilter("RESOLVED,CLOSED");
                setAmountFilter("with_amount");
                setAssignFilter("");
                setActiveTab("closed");
                setPage(1);
              }}
            >
              <span className="zp-finance-label">Amount Recovered</span>
              <span className="zp-finance-value" style={{ color: "#10b981" }}>{formatINR(amountRecovered)}</span>
            </div>
            <div className="zp-finance-divider" />
            <div className="zp-finance-row">
              <span className="zp-finance-label">Recovery Rate</span>
              <span className="zp-finance-value" style={{ color: "#3b82f6", fontWeight: 700 }}>
                {totalAmount > 0 ? Math.round((amountRecovered / totalAmount) * 100) : 0}%
              </span>
            </div>
            <div 
              className={`zp-finance-row clickable ${amountFilter === "with_amount" && statusFilter === "" ? "active" : ""}`}
              onClick={() => handleAmountClick("with_amount")}
            >
              <span className="zp-finance-label">Paras with Amount</span>
              <span className="zp-finance-value">{stats.financials.paras_with_amount}</span>
            </div>
            <div 
              className={`zp-finance-row clickable ${amountFilter === "without_amount" ? "active" : ""}`}
              onClick={() => handleAmountClick("without_amount")}
            >
              <span className="zp-finance-label">Paras without Amount</span>
              <span className="zp-finance-value">{stats.financials.paras_without_amount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Zone Details ── */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <h3>Zone Details</h3>
        <div className="detail-grid">
          <div className="detail-item">
            <span className="detail-label">{t("zone")}</span>
            <strong className="detail-value">{zone.name}</strong>
          </div>
          <div className="detail-item">
            <span className="detail-label">{t("code")}</span>
            <span className="detail-value">{zone.code}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">{t("cugNumber")}</span>
            <span className="detail-value">{zone.cug_number || "—"}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">{t("acOffice")}</span>
            <span className="detail-value">{zone.ac_office || "—"}</span>
          </div>
        </div>
      </div>

      {/* ── Para Lists ── */}
      <div className="card">
        {/* Tab header */}
        <div className="zp-tab-header">
          <div className="zp-tabs">
            {[
              { key: "pending", label: "⏳ Pending",  count: stats.status_counts.PENDING + stats.status_counts.IN_PROGRESS + stats.status_counts.REOPENED, color: "#f59e0b", statusVal: "PENDING,IN_PROGRESS,REOPENED" },
              { key: "closed",  label: "🔒 Closed",   count: stats.status_counts.RESOLVED + stats.status_counts.CLOSED,              color: "#10b981", statusVal: "RESOLVED,CLOSED" },
              { key: "new",     label: "🆕 New Paras", count: stats.status_counts.PENDING,                                  color: "#3b82f6", statusVal: "PENDING" },
              { key: "all",     label: "🌐 All Paras", count: stats.total_paras,                                  color: "#6366f1", statusVal: "" },
            ].map(tab => (
              <button
                key={tab.key}
                className={`zp-tab-btn ${activeTab === tab.key ? "active" : ""}`}
                style={activeTab === tab.key ? { borderColor: tab.color, color: tab.color } : {}}
                onClick={() => {
                  setActiveTab(tab.key);
                  setStatusFilter(tab.statusVal);
                  setPage(1);
                }}
              >
                {tab.label}
                <span className="zp-tab-count" style={activeTab === tab.key ? { background: tab.color } : {}}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => {
              navigate(`/paras?zone=${id}&status=${statusFilter}`);
            }}
          >
            View in Table &rarr;
          </button>
        </div>

        {/* Tab content */}
        {parasLoading ? (
          <div className="zp-empty-tab" style={{ padding: "40px" }}>
            <div className="spinner" style={{ borderTopColor: "#3b82f6" }}></div>
            <p>Loading paras...</p>
          </div>
        ) : paras.length === 0 ? (
          <div className="zp-empty-tab">
            <span style={{ fontSize: 32 }}>
              {activeTab === "closed" ? "🎉" : "✅"}
            </span>
            <p>
              {activeTab === "closed"
                ? "No closed paras found."
                : activeTab === "pending"
                ? "No pending paras — great work!"
                : "No matching paras found."}
            </p>
          </div>
        ) : (
          <div>
            <div className="zp-para-list">
              {paras.map(p => {
                const sc = statusBreakdown.find(s => s.status === p.status) || { color: "#64748b", bg: "#f1f5f9", icon: "•" };
                return (
                  <div key={p.id} className="zp-para-row" onClick={() => navigate(`/paras/${p.id}`)}>
                    <div className="zp-para-left">
                      <span className="zp-para-icon">{sc.icon}</span>
                      <div>
                        <div className="zp-para-no">
                          {p.para_no}
                          <span className="zp-para-year"> · {p.audit_year}</span>
                        </div>
                        <div className="zp-para-dept">{p.department}</div>
                      </div>
                    </div>
                    <div className="zp-para-right">
                      {p.amount > 0 && (
                        <span className="zp-para-amount">{formatINR(p.amount)}</span>
                      )}
                      <span className="zp-para-badge" style={{ background: sc.bg, color: sc.color }}>
                        {p.status.replace(/_/g, " ")}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={setPage}
              style={{ justifyContent: "center", gap: "6px", marginTop: "1.5rem" }}
            />
          </div>
        )}
      </div>

      {showEdit && (
        <ZoneForm
          zone={zone}
          onClose={() => setShowEdit(false)}
          onSuccess={() => {
            setShowEdit(false);
            fetchZoneAndStats();
          }}
        />
      )}
    </div>
  );
}
